import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrandLogo from "../common/BrandLogo";
import { useI18n } from "../../lib/i18n";
import api from "../../lib/api";

/* Fallback nav (only used before the DB /nav-items response arrives, so users
   never see a naked logo bar). Matches the seeded defaults. */
const FALLBACK_NAV = [
  { url: "/", i18n_key: "nav.home" },
  { url: "/about", i18n_key: "nav.about" },
  { url: "/companies", i18n_key: "nav.companies" },
  { url: "/projects", i18n_key: "nav.projects" },
  { url: "/media", i18n_key: "nav.media" },
  { url: "/careers", i18n_key: "nav.careers" },
  { url: "/contact", i18n_key: "nav.contact" },
];

const Header = ({ settings = {}, ready = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navItems, setNavItems] = useState(null);
  const location = useLocation();
  const { t, lang, setLang, pick } = useI18n();

  // Fetch dynamic nav from admin CMS. Falls back to FALLBACK_NAV until loaded.
  useEffect(() => {
    let cancelled = false;
    api.get("/public/nav-items?show_in=header")
      .then((r) => { if (!cancelled) setNavItems(Array.isArray(r.data) ? r.data : []); })
      .catch((err) => {
        console.warn("[Header] Failed to load nav items:", err?.message);
        if (!cancelled) setNavItems([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Build tree: parents (parent_id null) sorted by order, each with .children[]
  const nav = useMemo(() => {
    if (!navItems || navItems.length === 0) {
      // Fallback until DB nav loads (or if it's empty)
      return FALLBACK_NAV.map((n) => ({
        id: n.url,
        url: n.url,
        i18n_key: n.i18n_key,
        label: {},
        target: "_self",
        children: [],
      }));
    }
    const byParent = new Map();
    navItems.forEach((n) => {
      const key = n.parent_id || "_root";
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(n);
    });
    byParent.forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    const roots = byParent.get("_root") || [];
    return roots.map((r) => ({ ...r, children: byParent.get(r.id) || [] }));
  }, [navItems]);

  const labelOf = (item) => {
    if (item.i18n_key) return t(item.i18n_key);
    const val = pick(item.label);
    return val || t(item.i18n_key || "");
  };

  const isExternal = (url) => /^https?:\/\//i.test(url);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Transparent-over-hero applies on Home when not scrolled and menu closed
  const overHero = location.pathname === "/" && !scrolled && !mobileOpen;
  const brandName = settings?.company_name || "Fırat Construction Yapı A.Ş.";

  const headerShellClass = overHero
    ? "bg-transparent"
    : `bg-white ${scrolled ? "shadow-[0_2px_24px_rgba(0,0,0,0.08)]" : "shadow-[0_1px_0_rgba(0,0,0,0.06)]"}`;
  const textCls = overHero ? "text-white" : "text-brand-dark";
  const dividerCls = overHero ? "bg-white/25" : "bg-black/15";
  const langInactive = overHero ? "text-white/85 hover:text-white" : "text-brand-dark hover:text-brand-navy";
  const langActive = overHero ? "text-white font-semibold" : "text-brand-primary font-semibold";
  const ctaCls = overHero
    ? "text-white border-white/50 hover:bg-white hover:text-brand-navy"
    : "text-brand-navy border-brand-navy hover:bg-brand-navy hover:text-white";
  const navInactive = overHero ? "text-white/85 hover:text-white" : "text-brand-dark hover:text-brand-navy";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-500 ${headerShellClass}`}
    >
      <div className="container-x h-[104px] md:h-28 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label={brandName} className="flex items-center flex-shrink-0">
          <BrandLogo place="header" onDark={overHero} testId="header-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-8" data-testid="primary-nav">
          {nav.map((n) => {
            const external = isExternal(n.url);
            const linkProps = external
              ? { href: n.url, target: n.target || "_blank", rel: "noopener noreferrer" }
              : { to: n.url };
            const Tag = external ? "a" : NavLink;
            return (
              <div key={n.id || n.url} className="relative group">
                <Tag
                  {...linkProps}
                  end={n.url === "/"}
                  data-testid={`nav-${(n.i18n_key || n.url).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                  className={({ isActive } = {}) =>
                    `nav-link whitespace-nowrap uppercase tracking-[0.2em] text-[0.72rem] font-medium transition-colors duration-300 ${navInactive} ${isActive ? "active" : ""}`
                  }
                >
                  {labelOf(n)}
                </Tag>
                {n.children && n.children.length > 0 ? (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="bg-white shadow-2xl border border-black/5 min-w-[240px] py-2">
                      {n.children.map((c) => {
                        const cExternal = isExternal(c.url);
                        const linkClass = "block px-5 py-3 text-[0.72rem] tracking-[0.2em] uppercase text-brand-dark hover:text-white hover:bg-brand-navy transition-colors duration-200";
                        return cExternal ? (
                          <a key={c.id} href={c.url} target={c.target || "_blank"} rel="noopener noreferrer" className={linkClass}>
                            {labelOf(c)}
                          </a>
                        ) : (
                          <Link key={c.id} to={c.url} className={linkClass}>
                            {labelOf(c)}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className={`flex items-center gap-3 md:gap-5 ${textCls}`}>
          <div
            className="hidden md:flex items-center gap-1 text-[0.7rem] tracking-[0.28em] uppercase"
            data-testid="lang-switcher"
          >
            {["tr", "en", "ar"].map((code, i) => (
              <React.Fragment key={code}>
                {i > 0 ? <span className="opacity-30">/</span> : null}
                <button
                  type="button"
                  className={`px-1 transition-colors duration-300 ${lang === code ? langActive : langInactive}`}
                  onClick={() => setLang(code)}
                  data-testid={`lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className={`hidden md:block w-px h-6 transition-colors duration-500 ${dividerCls}`} />

          <Link
            to="/contact"
            data-testid="header-contact-cta"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-[0.7rem] tracking-[0.22em] uppercase font-medium border whitespace-nowrap transition-colors duration-300 ${ctaCls}`}
          >
            {t("home.contactCta")}
          </Link>

          <button
            type="button"
            className={`lg:hidden p-2 -mr-2 transition-colors duration-300 ${overHero ? "text-white" : "text-brand-navy"}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} strokeWidth={1.5} /> : <MenuIcon size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white border-t border-black/5"
            data-testid="mobile-menu"
          >
            <div className="container-x py-6 flex flex-col gap-4">
              {nav.map((n) => {
                const external = isExternal(n.url);
                return (
                  <React.Fragment key={n.id || n.url}>
                    {external ? (
                      <a href={n.url} target={n.target || "_blank"} rel="noopener noreferrer" className="text-brand-navy uppercase tracking-[0.22em] text-sm font-medium">
                        {labelOf(n)}
                      </a>
                    ) : (
                      <NavLink
                        to={n.url}
                        end={n.url === "/"}
                        data-testid={`mobile-nav-${(n.i18n_key || n.url).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className={({ isActive }) => `text-brand-navy uppercase tracking-[0.22em] text-sm font-medium ${isActive ? "text-brand-primary" : ""}`}
                      >
                        {labelOf(n)}
                      </NavLink>
                    )}
                    {(n.children || []).map((c) => {
                      const cExternal = isExternal(c.url);
                      return cExternal ? (
                        <a key={c.id} href={c.url} target={c.target || "_blank"} rel="noopener noreferrer" className="ml-4 text-brand-medium uppercase tracking-[0.22em] text-xs font-medium hover:text-brand-navy">
                          → {labelOf(c)}
                        </a>
                      ) : (
                        <Link key={c.id} to={c.url} className="ml-4 text-brand-medium uppercase tracking-[0.22em] text-xs font-medium hover:text-brand-navy">
                          → {labelOf(c)}
                        </Link>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <div className="flex items-center gap-3 pt-4 border-t border-black/5">
                {["tr", "en", "ar"].map((code, i) => (
                  <React.Fragment key={code}>
                    {i > 0 ? <span className="opacity-30">/</span> : null}
                    <button
                      type="button"
                      onClick={() => setLang(code)}
                      className={`text-[0.7rem] tracking-[0.28em] uppercase ${
                        lang === code ? "text-brand-primary font-semibold" : "text-brand-dark"
                      }`}
                      data-testid={`mobile-lang-${code}`}
                    >
                      {code.toUpperCase()}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};

export default Header;
